import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Plus, Trash2 } from "react-feather";
import { useMasterPermissions } from "../useMasterPermissions";
import { useGetTaxSlabsQuery, useSaveTaxSlabMutation, useDeleteTaxSlabMutation } from "./taxApi";
import {
  bandsToRows,
  rowsToBands,
  computePaye,
  defaultStatutory,
  explainBands,
  roundAway,
  SHIF_RATE,
  HOUSING_LEVY_RATE,
  NSSF_RATE,
} from "./payeCalc";

// PAYE bands + personal relief. One fixed config, not a normal master.
//
// Entry is KRA's own published table - "first 24,000 at 10%, next 8,333 at
// 25%, ... above 800,000 at 35%" - and the threshold/cumulative shape that
// Tax_Master actually stores is derived from it (see payeCalc.js). The old
// page asked for the stored shape directly, which meant typing each band's
// rate one row above where it belongs.
//
// Reading requires Tax.View (route-gated); every write is Tax.Edit +
// all-branch, there is no Tax.Create.
const money = (v) => (Number.isFinite(v) ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-");

const TaxPage = () => {
  const { canEdit: canManage } = useMasterPermissions("Tax");
  const { data: slabs = [], isLoading } = useGetTaxSlabsQuery();
  const [saveTaxSlab, { isLoading: isSaving }] = useSaveTaxSlabMutation();
  const [deleteTaxSlab] = useDeleteTaxSlabMutation();

  const [bands, setBands] = useState([{ slNo: null, width: "", rate: "" }]);
  const [reliefAmount, setReliefAmount] = useState("");
  const [pendingDeletes, setPendingDeletes] = useState([]);
  const [isReadOnly, setIsReadOnly] = useState(true);

  // Sample calculator - independent of the save flow, never writes anything.
  const [sampleGross, setSampleGross] = useState("");
  const [override, setOverride] = useState({ nssf: "", shif: "", levy: "" });

  const syncFromServer = () => {
    if (slabs.length > 0) {
      setBands(rowsToBands(slabs));
      setReliefAmount(String([...slabs].sort((a, b) => a.slNo - b.slNo)[0].reliefAmount ?? ""));
      setIsReadOnly(true);
    } else {
      setBands([{ slNo: null, width: "", rate: "" }]);
      setReliefAmount("");
      setIsReadOnly(false);
    }
    setPendingDeletes([]);
  };

  useEffect(() => {
    if (!isLoading) syncFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, slabs]);

  const editable = canManage && !isReadOnly;

  // Everything below is derived live from what is on screen, so the preview
  // reflects unsaved edits - that is the point of it.
  const rows = useMemo(() => bandsToRows(bands), [bands]);

  const updateBand = (index, key, value) =>
    setBands((prev) => prev.map((b, i) => (i === index ? { ...b, [key]: value } : b)));

  const addBand = () =>
    setBands((prev) => {
      // A new band is inserted BEFORE the open-ended top one, which always stays last.
      const next = [...prev];
      next.splice(Math.max(0, next.length - 1), 0, { slNo: null, width: "", rate: "" });
      return next;
    });

  const removeBand = (index) =>
    setBands((prev) => {
      const b = prev[index];
      if (b.slNo != null) setPendingDeletes((d) => [...d, b.slNo]);
      return prev.filter((_, i) => i !== index);
    });

  const handleSave = async () => {
    if (bands.length < 2) return toast.error("Add at least one band plus the top rate");
    const badRate = bands.some((b) => b.rate === "" || Number.isNaN(Number(b.rate)));
    if (badRate) return toast.error("Every band needs a rate");
    const badWidth = bands.slice(0, -1).some((b) => b.width === "" || !(Number(b.width) > 0));
    if (badWidth) return toast.error("Every band except the top one needs a width above zero");

    try {
      for (const slNo of pendingDeletes) {
        // eslint-disable-next-line no-await-in-loop
        await deleteTaxSlab(slNo).unwrap();
      }
      for (const row of rows) {
        // eslint-disable-next-line no-await-in-loop
        await saveTaxSlab({
          slNo: row.slNo,
          taxFormulaAmount: row.taxFormulaAmount,
          taxAmount: row.taxAmount,
          deductionPercentage: row.deductionPercentage,
          reliefAmount: Number(reliefAmount) || 0,
          // Cumulative tax at this threshold. The server no longer derives it.
          dedAmount: row.dedAmount,
        }).unwrap();
      }
      toast.success("Tax bands saved");
    } catch (error) {
      toast.error(error?.message || "Could not save tax bands");
    }
  };

  // ---- sample calculation ----
  const auto = defaultStatutory(sampleGross);
  const used = {
    nssf: override.nssf === "" ? auto.nssf : Number(override.nssf) || 0,
    shif: override.shif === "" ? auto.shif : Number(override.shif) || 0,
    levy: override.levy === "" ? auto.levy : Number(override.levy) || 0,
  };
  const gross = Number(sampleGross) || 0;
  const taxableBase = roundAway(gross - used.nssf - used.shif - used.levy, 2);
  const result = computePaye(taxableBase, rows, Number(reliefAmount) || 0);
  const breakdown = explainBands(taxableBase, bands);
  const netPay = roundAway(gross - used.nssf - used.shif - used.levy - result.paye, 2);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Payee (Tax)</h4>
              <h6>PAYE tax bands and personal relief</h6>
            </div>
          </div>
        </div>

        {!canManage && (
          <div className="alert alert-info">
            You have read-only access. Managing tax bands requires Tax.Edit and an all-branch administrator.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-1">Tax Bands</h5>
            <p className="text-muted small mb-3">
              Enter the schedule exactly as KRA publishes it. The last row is the open-ended top rate and has no
              width. Thresholds and cumulative tax are worked out below.
            </p>

            <div className="table-responsive mb-3">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>Band</th>
                    <th>Monthly band (KES)</th>
                    <th style={{ width: 130 }}>Rate %</th>
                    <th>Up to</th>
                    <th>Tax in band</th>
                    <th>Cumulative tax</th>
                    {editable && <th style={{ width: 40 }} />}
                  </tr>
                </thead>
                <tbody>
                  {bands.map((band, index) => {
                    const isTop = index === bands.length - 1;
                    const upTo = isTop ? null : rows[index]?.taxAmount;
                    const taxInBand = isTop ? null : (Number(band.width) || 0) * (Number(band.rate) || 0) / 100;
                    return (
                      <tr key={band.slNo ?? `b-${index}`}>
                        <td>{index + 1}</td>
                        <td>
                          {isTop ? (
                            <span className="text-muted">
                              Above {(rows[rows.length - 1]?.taxAmount ?? 0).toLocaleString()} (no upper limit)
                            </span>
                          ) : (
                            <input
                              type="number"
                              className="form-control"
                              value={band.width}
                              onChange={(e) => updateBand(index, "width", e.target.value)}
                              readOnly={!editable}
                              disabled={!editable}
                            />
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            value={band.rate}
                            onChange={(e) => updateBand(index, "rate", e.target.value)}
                            readOnly={!editable}
                            disabled={!editable}
                          />
                        </td>
                        <td>{isTop ? "-" : (upTo ?? 0).toLocaleString()}</td>
                        <td>{isTop ? "-" : money(taxInBand)}</td>
                        <td>{isTop ? "-" : money(rows[index]?.dedAmount)}</td>
                        {editable && (
                          <td>
                            <button
                              type="button"
                              className="btn btn-link p-0 text-danger"
                              onClick={() => removeBand(index)}
                              disabled={bands.length <= 2}
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {editable && (
              <button type="button" className="btn btn-outline-primary btn-sm mb-3" onClick={addBand}>
                <Plus size={14} className="me-1" />
                Add Band
              </button>
            )}

            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Personal Relief (per month)</label>
                <input
                  type="number"
                  className="form-control"
                  value={reliefAmount}
                  onChange={(e) => setReliefAmount(e.target.value)}
                  readOnly={!editable}
                  disabled={!editable}
                />
                <small className="text-muted">Deducted from the tax due, not from pay. Applies to every band.</small>
              </div>
            </div>

            {canManage && (
              <div className="d-flex gap-2">
                {!isReadOnly && (
                  <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                    Save
                  </button>
                )}
                {isReadOnly && (
                  <button type="button" className="btn btn-outline-primary" onClick={() => setIsReadOnly(false)}>
                    Edit
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={syncFromServer}>
                  Clear
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ---------------- Sample calculation ---------------- */}
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-1">Sample Calculation</h5>
            <p className="text-muted small mb-3">
              Checks the bands above against a sample salary, using the same lookup payroll uses. Nothing is saved.
              Edits to the bands are reflected here before you save them.
            </p>

            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Gross Pay</label>
                <input
                  type="number"
                  className="form-control"
                  value={sampleGross}
                  onChange={(e) => setSampleGross(e.target.value)}
                  placeholder="e.g. 80000"
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">NSSF</label>
                <input
                  type="number"
                  className="form-control"
                  value={override.nssf}
                  onChange={(e) => setOverride((o) => ({ ...o, nssf: e.target.value }))}
                  placeholder={String(auto.nssf)}
                />
                <small className="text-muted">{NSSF_RATE}%, capped 540 - 6,480</small>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">SHIF</label>
                <input
                  type="number"
                  className="form-control"
                  value={override.shif}
                  onChange={(e) => setOverride((o) => ({ ...o, shif: e.target.value }))}
                  placeholder={String(auto.shif)}
                />
                <small className="text-muted">{SHIF_RATE}% of gross</small>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Housing Levy</label>
                <input
                  type="number"
                  className="form-control"
                  value={override.levy}
                  onChange={(e) => setOverride((o) => ({ ...o, levy: e.target.value }))}
                  placeholder={String(auto.levy)}
                />
                <small className="text-muted">{HOUSING_LEVY_RATE}% of gross</small>
              </div>
            </div>

            {gross > 0 && (
              <div className="row">
                <div className="col-md-6">
                  <table className="table table-sm mb-3">
                    <tbody>
                      <tr>
                        <td>Gross pay</td>
                        <td className="text-end">{money(gross)}</td>
                      </tr>
                      <tr>
                        <td className="ps-4 text-muted">less NSSF</td>
                        <td className="text-end text-muted">({money(used.nssf)})</td>
                      </tr>
                      <tr>
                        <td className="ps-4 text-muted">less SHIF</td>
                        <td className="text-end text-muted">({money(used.shif)})</td>
                      </tr>
                      <tr>
                        <td className="ps-4 text-muted">less Housing Levy</td>
                        <td className="text-end text-muted">({money(used.levy)})</td>
                      </tr>
                      <tr className="fw-bold border-top">
                        <td>Taxable pay</td>
                        <td className="text-end">{money(taxableBase)}</td>
                      </tr>
                      <tr>
                        <td>Tax before relief</td>
                        <td className="text-end">{money(result.gross)}</td>
                      </tr>
                      <tr>
                        <td className="ps-4 text-muted">less personal relief</td>
                        <td className="text-end text-muted">({money(Number(reliefAmount) || 0)})</td>
                      </tr>
                      <tr className="fw-bold border-top">
                        <td>PAYE payable</td>
                        <td className="text-end">{money(result.paye)}</td>
                      </tr>
                      <tr>
                        <td>Net pay</td>
                        <td className="text-end">{money(netPay)}</td>
                      </tr>
                    </tbody>
                  </table>

                  {result.matched === null && taxableBase > 0 && (
                    <div className="alert alert-secondary py-2 small">
                      Taxable pay is below the first threshold, so no band matches and PAYE is nil.
                    </div>
                  )}
                </div>

                <div className="col-md-6">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Band</th>
                        <th className="text-end">Rate</th>
                        <th className="text-end">Taxed here</th>
                        <th className="text-end">Tax</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakdown.map((b) => (
                        <tr key={b.label}>
                          <td>{b.label}</td>
                          <td className="text-end">{b.rate}%</td>
                          <td className="text-end">{money(b.taxed)}</td>
                          <td className="text-end">{money(b.tax)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <small className="text-muted">
                    Band-by-band view of the same figure. Payroll itself does not add these up - it reads the
                    cumulative tax stored against the matched threshold, which is what the Tax Bands table above
                    shows.
                  </small>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxPage;
